import { useState, useRef, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { brandInfo } from '@/mocks/homeData';

export default function AboutPage() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');
  const contactFormRef = useRef<HTMLFormElement>(null);

  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = contactFormRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setContactStatus('success');
      form.reset();
      return;
    }

    const name = (formData.get('name') as string || '').trim();
    const email = (formData.get('email') as string || '').trim();
    const message = (formData.get('message') as string || '').trim();

    if (!name || !email || !message) {
      setContactStatus('error');
      setContactError('请填写所有必填字段');
      return;
    }

    if (message.length > 500) {
      setContactStatus('error');
      setContactError('留言内容不能超过500个字符');
      return;
    }

    setContactStatus('loading');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q1e8nnahb7382ov4lg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ name, email, message }).toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setContactStatus('error');
          setContactError('提交失败，请稍后重试');
        } else {
          setContactStatus('error');
          setContactError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setContactStatus('success');
      form.reset();
    }
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="pt-20 md:pt-24 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 py-3" aria-label="面包屑导航">
              <a href="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</a>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-950 font-medium">关于我们</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="w-full py-10 md:py-16 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 text-center">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-primary-500 flex items-center justify-center mx-auto mb-5">
              <i className="ri-heart-pulse-line text-2xl text-background-50"></i>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              关于 {brandInfo.name}
            </h1>
            <p className="text-base md:text-lg text-primary-600 font-medium mb-4">
              {brandInfo.slogan}
            </p>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {brandInfo.mission}
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  我们的故事
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {brandInfo.story}
                </p>
                <div className="flex items-center gap-6 pt-2">
                  <div className="text-center">
                    <div className="font-heading text-2xl font-bold text-primary-500">{brandInfo.founded}</div>
                    <div className="text-xs text-foreground-500 mt-1">创立时间</div>
                  </div>
                  <div className="w-px h-12 bg-background-300"></div>
                  <div className="text-center">
                    <div className="font-heading text-2xl font-bold text-primary-500">500+</div>
                    <div className="text-xs text-foreground-500 mt-1">科普视频</div>
                  </div>
                  <div className="w-px h-12 bg-background-300"></div>
                  <div className="text-center">
                    <div className="font-heading text-2xl font-bold text-primary-500">200万+</div>
                    <div className="text-xs text-foreground-500 mt-1">累计观看</div>
                  </div>
                </div>
              </div>
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20team%20workspace%20with%20warm%20peach%20and%20cream%20interior%2C%20soft%20natural%20light%20from%20large%20window%2C%20modern%20clean%20design%2C%20calm%20medical%20office%20atmosphere%2C%20feminine%20wellness%20workspace%2C%20editorial%20photography&width=800&height=600&seq=about-story&orientation=landscape"
                  alt="爱液视频团队"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                我们的价值观
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {brandInfo.values.map((val) => (
                <div key={val.title} className="bg-background-100 rounded-xl p-5 md:p-6 text-center">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${val.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">{val.title}</h3>
                  <p className="text-sm text-foreground-600">{val.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section id="reputation" className="w-full py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                用户口碑
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mt-2">
                听听她们怎么说
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {brandInfo.reviews.map((review, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, starIdx) => (
                      <i
                        key={starIdx}
                        className={`text-sm ${starIdx < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{review.comment}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-primary-600">{review.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{review.name}</p>
                      <p className="text-xs text-foreground-500">{review.age}岁</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-600 mb-6 leading-relaxed">
                  如果你有任何问题、建议或合作意向，欢迎通过以下方式联系我们，或填写右侧表单，我们会尽快回复。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center">
                      <i className="ri-mail-line text-secondary-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm font-medium text-foreground-950">contact@aiyeshipin.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center">
                      <i className="ri-phone-line text-secondary-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电话</p>
                      <p className="text-sm font-medium text-foreground-950">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center">
                      <i className="ri-map-pin-line text-secondary-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm font-medium text-foreground-950">北京市朝阳区建国路88号SOHO现代城A座</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form ref={contactFormRef} onSubmit={handleContactSubmit} data-readdy-form="" className="bg-background-100 rounded-xl p-5 md:p-6 space-y-4">
                  <div className="spam-protection-field">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名 <span className="text-primary-500">*</span></label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      placeholder="请输入你的姓名"
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱 <span className="text-primary-500">*</span></label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      placeholder="请输入你的邮箱"
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言 <span className="text-primary-500">*</span></label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      placeholder="请输入你想说的话（500字以内）"
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={contactStatus === 'loading'}
                    className="w-full px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {contactStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中
                      </span>
                    ) : (
                      '发送留言'
                    )}
                  </button>

                  {contactStatus === 'success' && (
                    <p className="text-sm text-secondary-600 font-medium text-center">
                      <i className="ri-checkbox-circle-line mr-1"></i>
                      留言已发送！我们会尽快回复你。
                    </p>
                  )}
                  {contactStatus === 'error' && (
                    <p className="text-sm text-red-500 font-medium text-center">
                      <i className="ri-error-warning-line mr-1"></i>
                      {contactError}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}